import {
  deleteDoc,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
  type DocumentReference,
} from "firebase/firestore";
import { useCallback, useEffect, useRef, useState } from "react";

import { db } from "integrations/firebase/firestore";

import {
  PERSON_NOTE_MAX_LENGTH,
  personNoteId,
  type NoteEventSnapshot,
} from "./schemas";

/** Quiet time after the last keystroke before the note is written. */
export const AUTOSAVE_DELAY_MS = 800;

export type NoteSaveStatus =
  | "loading"
  | "load-error"
  | "idle"
  | "dirty"
  /** Emptied, not yet removed: the delete waits for close. */
  | "cleared"
  | "saving"
  | "saved"
  /** A stored note was deleted because it was emptied and closed. */
  | "removed"
  | "error";

/**
 * How far a save may go.
 *
 * `keep` never deletes: the debounce, blur and the page going to the
 * background. `close` also deletes an emptied note: unmount (the sheet
 * closing, Done on the notes page) and `pagehide`.
 *
 * The split exists because a delete cannot be undone for everybody. A first
 * note needs membership of a live event, so once the author has left the
 * event or it was deleted, a note deleted by select-all, backspace and a
 * pause could never be written again. Deleting only on close gives the
 * author until then to retype.
 */
type SaveMode = "keep" | "close";

export interface PersonNoteTarget extends NoteEventSnapshot {
  uid: string;
  eventId: string;
  attendeeId: string;
  personName: string;
}

export interface PersonNoteState {
  status: NoteSaveStatus;
  text: string;
  setText: (value: string) => void;
  /** Write now, skipping the quiet period, without deleting. Used on blur. */
  flush: () => void;
  /** Try the last failed load or save again. */
  retry: () => void;
}

/**
 * One private note, autosaved.
 *
 * Read once rather than listened to: a live listener would overwrite the
 * textarea with the server copy of a write this hook itself just sent.
 *
 * Writes are serialized through one promise chain, so a create still in
 * flight is never overtaken by an update that would hit a missing document.
 * Each write picks create, update or delete from what the previous one left
 * behind, and when a write fails it re-reads the document once and tries
 * again from what is really there: a note created or deleted on another
 * device would otherwise leave this editor failing on every save.
 *
 * Pending text is saved when the component unmounts, when the page is hidden
 * and on `pagehide`, so closing the sheet or the tab inside the quiet period
 * keeps the last keystrokes, and a save that failed gets one more attempt.
 */
export function usePersonNote(target: PersonNoteTarget): PersonNoteState {
  const { uid, eventId, attendeeId } = target;
  const [status, setStatus] = useState<NoteSaveStatus>("loading");
  const [text, setTextState] = useState("");
  const [loadAttempt, setLoadAttempt] = useState(0);

  const textRef = useRef("");
  const savedRef = useRef("");
  const existsRef = useRef(false);
  const loadedRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const chainRef = useRef<Promise<void>>(Promise.resolve());
  const targetRef = useRef(target);
  targetRef.current = target;

  useEffect(() => {
    let cancelled = false;
    loadedRef.current = false;
    setStatus("loading");
    getDoc(
      doc(db, "users", uid, "person_notes", personNoteId(eventId, attendeeId)),
    )
      .then((snapshot) => {
        if (cancelled) return;
        const body = snapshot.exists()
          ? String(snapshot.get("body") ?? "")
          : "";
        existsRef.current = snapshot.exists();
        savedRef.current = body;
        textRef.current = body;
        loadedRef.current = true;
        setTextState(body);
        setStatus("idle");
      })
      .catch(() => {
        if (!cancelled) setStatus("load-error");
      });
    return () => {
      cancelled = true;
    };
  }, [uid, eventId, attendeeId, loadAttempt]);

  const save = useCallback((mode: SaveMode) => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (!loadedRef.current) return;
    chainRef.current = chainRef.current.then(async () => {
      const body = textRef.current;
      const empty = body.trim() === "";
      if (body === savedRef.current || (empty && !existsRef.current)) {
        // Nothing stored differs from the text. An emptied note that was
        // never stored has nothing to remove either.
        savedRef.current = body;
        setStatus((current) =>
          current === "dirty" || current === "cleared" || current === "error"
            ? empty
              ? "idle"
              : "saved"
            : current,
        );
        return;
      }
      if (empty && mode === "keep") {
        setStatus("cleared");
        return;
      }
      const current = targetRef.current;
      const ref = doc(
        db,
        "users",
        current.uid,
        "person_notes",
        personNoteId(current.eventId, current.attendeeId),
      );
      setStatus("saving");
      try {
        try {
          await write(ref, body, current, existsRef);
        } catch {
          // Maybe another device created or deleted the note since it was
          // read here. Look, then try once more from what is there.
          existsRef.current = (await getDoc(ref)).exists();
          await write(ref, body, current, existsRef);
        }
        savedRef.current = body;
        if (textRef.current !== body) setStatus("dirty");
        else setStatus(empty ? "removed" : "saved");
      } catch {
        setStatus("error");
      }
    });
  }, []);

  const setText = useCallback(
    (value: string) => {
      const capped = value.slice(0, PERSON_NOTE_MAX_LENGTH);
      textRef.current = capped;
      setTextState(capped);
      setStatus("dirty");
      if (timerRef.current !== null) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => save("keep"), AUTOSAVE_DELAY_MS);
    },
    [save],
  );

  // The tab going to the background may be the last chance a phone gives us
  // (it can be discarded without unload), so save the text but keep an
  // emptied note: the author may come back and retype. `pagehide` is the
  // page actually going away, which counts as closing.
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "hidden") save("keep");
    };
    const onPageHide = () => save("close");
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", onPageHide);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", onPageHide);
    };
  }, [save]);

  // Unmount is closing: the sheet closed or Done was pressed. `save` is a
  // no-op when nothing differs, and otherwise covers pending keystrokes, an
  // emptied note and one more attempt at a save that failed.
  useEffect(() => () => save("close"), [save]);

  const retry = useCallback(() => {
    if (!loadedRef.current) {
      setLoadAttempt((attempt) => attempt + 1);
      return;
    }
    save("keep");
  }, [save]);

  const flush = useCallback(() => save("keep"), [save]);

  return { status, text, setText, flush, retry };
}

async function write(
  ref: DocumentReference,
  body: string,
  target: PersonNoteTarget,
  existsRef: { current: boolean },
): Promise<void> {
  if (body.trim() === "") {
    if (existsRef.current) await deleteDoc(ref);
    existsRef.current = false;
    return;
  }
  const snapshotFields = {
    personName: target.personName.slice(0, 200),
    ...(target.eventTitle
      ? { eventTitle: target.eventTitle.slice(0, 200) }
      : {}),
    ...(target.eventSlug ? { eventSlug: target.eventSlug } : {}),
  };
  if (existsRef.current) {
    await updateDoc(ref, {
      body,
      ...snapshotFields,
      updatedAt: serverTimestamp(),
    });
    return;
  }
  await setDoc(ref, {
    eventId: target.eventId,
    attendeeId: target.attendeeId,
    ...snapshotFields,
    body,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  existsRef.current = true;
}
